import React, { useCallback, useState } from 'react'
import { IFetalDiagnosis, IFetchFetalDiagnosesParams } from '~/interfaces'
import { fetchFetalDiagnoses, message } from '~/services'

interface IFetalDiagnosesProviderProps {
  children: React.ReactNode
}

const defaultValue = {
  fetalDiagnoses: [] as IFetalDiagnosis[],
  isLoading: true,
  errorMessage: undefined
}

export interface IFetalDiagnosesContextHelpers {
  isLoading: boolean
  errorMessage: string | undefined
}

export interface IFetalDiagnosesContextProps extends IFetalDiagnosesContextHelpers {
  fetalDiagnoses: IFetalDiagnosis[]
  getFetalDiagnoses: (params: IFetchFetalDiagnosesParams) => void
}

const FetalDiagnosesContext = React.createContext<IFetalDiagnosesContextProps>({
  ...defaultValue
} as IFetalDiagnosesContextProps)

const FetalDiagnosesProvider = ({ children }: IFetalDiagnosesProviderProps): JSX.Element => {
  const [fetalDiagnoses, setFetalDiagnoses] = useState(defaultValue.fetalDiagnoses)
  const [isLoading, setIsLoading] = useState(defaultValue.isLoading)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(defaultValue.errorMessage)

  const getFetalDiagnoses = useCallback(async (params: IFetchFetalDiagnosesParams) => {
    try {
      const response = await fetchFetalDiagnoses(params)

      if (!response.length) {
        setErrorMessage(message('fetalDiagnoses.not_found'))
      }

      setFetalDiagnoses(response)
    } catch (error) {
      setErrorMessage(message('messages.genericUnexpectedError'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  return (
    <FetalDiagnosesContext.Provider
      value={{
        fetalDiagnoses,
        errorMessage,
        isLoading,
        getFetalDiagnoses
      }}
    >
      {children}
    </FetalDiagnosesContext.Provider>
  )
}

const useFetalDiagnoses = (): IFetalDiagnosesContextProps => {
  return React.useContext(FetalDiagnosesContext)
}

export { FetalDiagnosesContext, FetalDiagnosesProvider, useFetalDiagnoses }
